import { EventEmitter } from '@angular/core';
import { MessageService } from 'primeng/api';
import { lastValueFrom, Observable, Subject } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';

import { DatePipe } from '@angular/common';
import { Injectable } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';

import { SettingsService } from './settings.service';
import { User } from '../models/user';
import { Marshal } from '../models/marshal';
import { Race, SportType } from '../models/race';
import { RaceClass } from '../models/race-class';
import { RateData } from 'src/app/models/types';
import { DocType } from '../modules/race-form/models/doc-type';
import { SocketService } from './socket.service';
import { RaceDomainService } from '../domains/race.service';
import { ServiceDomainService } from '../domains/service.service';
import { AuthDomainService } from '../domains/auth.service';

const ERROR = 'ERROR';

@Injectable({
  providedIn: 'root',
})
export class RaceService {
  refreshRace: EventEmitter<void> = new EventEmitter<void>();
  toggleEvent: EventEmitter<boolean> = new EventEmitter<boolean>();
  raceIsRefreshed: EventEmitter<void> = new EventEmitter<void>();

  public repeatSource: Subject<Date[]> = new Subject();
  public repeat$: Observable<Race[] | undefined>;

  public classesSource: Subject<string> = new Subject();
  public repeatClasses$: Observable<RaceClass[]>;

  public showAuthModel: EventEmitter<void> = new EventEmitter();

  public raceList: Array<Race> | undefined = undefined;
  private raceListMap: Map<string, Race> = new Map();
  userAge: { age: number; str_age: string } = { age: 0, str_age: '' };
  input: any;

  constructor(
    private settingService: SettingsService,
    private datePipe: DatePipe,
    private socketService: SocketService,
    private messageService: MessageService,
    private raceDomainService: RaceDomainService,
    private serviceDomainService: ServiceDomainService,
    private authDomainService: AuthDomainService
  ) {
    this.socketService.addListener(ERROR, (err) => {
      this.messageService.clear();
      this.messageService.add({
        severity: 'warning',
        summary:
          this.settingService.translate[this.settingService.selectedLang][
          'error'
          ],
        detail: err,
      });
    });

    this.repeatClasses$ = this.classesSource.pipe(
      switchMap((rUid: string) => this.getClasses(rUid))
    );

    this.repeat$ = this.repeatSource.pipe(
      switchMap((dates) => this.getList(dates[0], dates[1], this.settingService.selectedRacesType.value)),
      map((arr: Race[]) => {
        // if (!this.user?.cUid) return arr;
        // if (this.settingService.selectedRacesType.value === 'org') {
        //   return arr.filter((race) => {
        //     return race.orgs.some((org) =>
        //       org.cUid.some((cUid) => cUid.cUid === this.user?.cUid)
        //     );
        //   });
        // }

        // if (this.settingService.selectedRacesType.value === 'comp') {
        //   return arr.filter((race) => {
        //     return race.isCompetitor;
        //   });
        // }

        return arr;
      }),
      map((arr: Race[]) => {
        if (!arr) return [];
        arr = arr.filter((item) => !this.raceListMap.has(item.rUid));
        if (!this.raceList) {
          this.raceList = [];
        }
        this.raceList = this.raceList?.concat(arr);
        this.raceList = this.raceList?.sort(
          (a, b) => b.date.getTime() - a.date.getTime()
        );
        this.raceList?.forEach((race) => {
          this.raceListMap.set(race.rUid, race);
        });
        return this.raceList;
      })
    );
  }

  // Получает список видов спорта
  public getSportTypes(): Observable<SportType[]> {
    return this.raceDomainService.getSportTypes();
  }

  // Получает спиоск типов документов, требуемых при регистрации
  public getDocTypes(): Observable<DocType[]> {
    return this.raceDomainService.getDocTypes();
  }

  // Очищает массив список гонок
  public emptyRaceList() {
    if (this.raceList) {
      this.raceList = [];
      this.raceListMap = new Map();
    }
  }

  // Возвращает список гонок за указанный период
  private getList(dateStart: Date, dateEnd: Date, type: string): Observable<Race[]> {
    const dateTo = this.datePipe.transform(dateEnd, 'yyyy-MM-dd');
    const dateFrom = this.datePipe.transform(dateStart, 'yyyy-MM-dd');

    return this.raceDomainService.getList(dateFrom!, dateTo!, type);
  }

  // Возвращает профиль гонки
  public getRace(rUid: string): Observable<Race> {
    return this.raceDomainService.getRace(rUid);
  }

  // Сохраняет профиль гонки
  saveRace(race: any) {
    return lastValueFrom(
      this.raceDomainService.saveRace(race, this.settingService.selectedLang)
    );
  }

  // Удаляет гонку
  deleteRace(rUid: string) {
    return this.raceDomainService.deleteRace(rUid).pipe(
      map((response) => {
        this.raceList?.splice(
          this.raceList.findIndex((race) => race.rUid == rUid),
          1
        );
        this.messageService.add({
          severity: 'success',
          summary: this.settingService.translate[this.settingService.selectedLang]['success'],
          detail: this.settingService.translate[this.settingService.selectedLang]['race_deleted'],
        });
        return response;
      }),
    );
  }

  // Копирует гонку
  copyRace(rUid: string, copyName: string, withDocs: boolean, withCompetitors: boolean) {
    return this.raceDomainService.copyRace(rUid, copyName, withDocs, withCompetitors).pipe(
      map((response) => {
        this.raceList?.unshift(response);
        this.messageService.add({
          severity: 'success',
          summary: this.settingService.translate[this.settingService.selectedLang]['success'],
          detail: this.settingService.translate[this.settingService.selectedLang]['race_copied'],
        });
        return response;
      }),
    );
  }

  // Получает список классов гонки
  private getClasses(ruid: string): Observable<RaceClass[]> {
    return this.raceDomainService.getClasses(ruid, 0);
  }

  // Получает класс гонки по clUid
  public getClass(rUid: string, clUid: string): Observable<RaceClass | null> {
    return this.getClasses(rUid).pipe(
      map((classes) => classes.find((c) => c.clUid === clUid) || null)
    );
  }

  // Сохраняет класс
  saveClass(raceClass: RaceClass, rUid: string | null, clUid: string | null) {
    return lastValueFrom(
      this.raceDomainService.saveRaceClass(raceClass, rUid, clUid)
    );
  }

  // Удаляет класс в гонке, если там никого нет
  deleteClass(rUid: string, clUid: string | undefined) {
    return this.raceDomainService.deleteClass(rUid, clUid || '');
  }

  // Сохраняет маршала
  saveMarshal(marshal: Marshal) {
    return this.raceDomainService.saveMarshal(marshal);
  }

  // Удаляет маршала
  deleteMarshal(marshal: Marshal) {
    return this.raceDomainService.deleteMarshal(marshal.rUid, marshal.number);
  }

  // Получает список отзывов на гонку
  getReviews(rUid: string) {
    return this.raceDomainService.getReviews(rUid).pipe(
      map((data) => {
        if (!data.rates.length) {
          data.rates = [
            { category: 0, rate: 0 },
            { category: 1, rate: 0 },
            { category: 2, rate: 0 },
            { category: 3, rate: 0 },
          ];
        } else {
          if (!data.rates.find((rate) => rate.category === 0)) {
            data.rates.push({ category: 0, rate: 0 });
          }
          if (!data.rates.find((rate) => rate.category === 1)) {
            data.rates.push({ category: 1, rate: 0 });
          }
          if (!data.rates.find((rate) => rate.category === 2)) {
            data.rates.push({ category: 2, rate: 0 });
          }
          if (!data.rates.find((rate) => rate.category === 3)) {
            data.rates.push({ category: 3, rate: 0 });
          }
        }
        return data;
      })
    );
  }

  // Добавляет отзыв на гонку
  addReview(rUid: string, rates: number[], comment: string) {
    return this.raceDomainService.addReview(rUid, rates, comment);
  }

  // Получить текст страницы для оргов/перс данных/...
  public getPage(page: string = ''): Observable<string> {
    return this.serviceDomainService.getPage(page);
  }

  // Сохранить текст страницы для оргов/перс данных/...
  public savePage(text: string, page: string = ''): Observable<void> {
    return this.serviceDomainService.savePage(text, page);
  }

  // Получает статус гонки
  public getRaceStatus(rUid: Race['rUid']): Observable<number> {
    return this.raceDomainService.getRaceState(rUid);
  }
}
